import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Bell, Bot, HandHeart, Image, Mail, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getPushAdminData } from "@/lib/push.functions";
import { ShortcutCard } from "@/components/admin/SectionShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDateBR } from "@/lib/formatDateBR";

export const Route = createFileRoute("/admin/marketing/")({
  component: MarketingDashboard,
});

const count = async (q: any) => (await q).count ?? 0;

function MarketingDashboard() {
  const { data } = useQuery({
    queryKey: ["admin-marketing-overview"],
    queryFn: async () => {
      const db = supabase as any;
      const nowIso = new Date().toISOString();
      const { data: s } = await supabase.auth.getSession();
      const [push, newsletter, banners, actions, campaigns] = await Promise.all([
        s.session ? getPushAdminData({ data: { accessToken: s.session.access_token } }).catch(() => null) : null,
        count(db.from("newsletter_subscribers").select("id", { count: "exact", head: true }).is("unsubscribed_at", null)),
        count(db.from("banners").select("id", { count: "exact", head: true }).eq("is_active", true)),
        count(db.from("special_brand_actions").select("id", { count: "exact", head: true }).eq("is_active", true)),
        db.from("marketing_campaigns").select("id, name, channel, status, created_at").order("created_at", { ascending: false }).limit(5),
      ]);
      void nowIso;
      return { push, newsletter, banners, actions, campaigns: (campaigns?.data ?? []) as any[] };
    },
  });

  const recent = [
    ...(data?.campaigns ?? []).map((c) => ({ id: c.id, title: c.name ?? "Campanha", kind: c.channel === "whatsapp" ? "WhatsApp" : "E-mail", at: c.created_at })),
    ...(data?.push?.campaigns ?? []).slice(0, 5).map((c: any) => ({ id: c.id, title: c.title, kind: "Push", at: c.created_at })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1)).slice(0, 8);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <ShortcutCard to="/admin/marketing/notificacoes" icon={Bell} title="Notificações" value={data?.push?.total ?? "—"} hint="aparelhos inscritos" />
        <ShortcutCard to="/admin/marketing/newsletter" icon={Mail} title="Newsletter" value={data?.newsletter ?? "—"} hint="inscritos ativos" />
        <ShortcutCard to="/admin/marketing/whatsapp" icon={MessageCircle} title="WhatsApp" value="Disparos" hint="enviar campanha" />
        <ShortcutCard to="/admin/marketing/banners" icon={Image} title="Banners" value={data?.banners ?? "—"} hint="banners ativos" />
        <ShortcutCard to="/admin/marketing/carrossel" icon={HandHeart} title="Carrossel" value={data?.actions ?? "—"} hint="ações especiais ativas" />
        <ShortcutCard to="/admin/marketing/automacoes" icon={Bot} title="Automações" value="Regras" hint="e-mail, push e WhatsApp" />
        <ShortcutCard to="/admin/marketing/selo" icon={BadgeCheck} title="Selo" value="HTML" hint="código para sites parceiros" />
      </div>
      <Card>
        <CardHeader><CardTitle>Últimos disparos</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {!recent.length && <p className="text-sm text-muted-foreground">Nenhum disparo ainda.</p>}
          {recent.map((r) => (
            <div key={`${r.kind}-${r.id}`} className="flex items-center justify-between gap-2 border-b py-2 text-sm">
              <span className="min-w-0 truncate font-medium">{r.title}</span>
              <span className="shrink-0 text-muted-foreground">{r.kind} · {formatDateBR(r.at)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
